import { Negotiation, NegotiationDetail, NegotiationStatus, OrderTerm } from 'api'
import { groupBy as _groupBy, isEqual, isNil, isNull, memoize } from 'lodash'
import { createSelector, Selector } from '@reduxjs/toolkit'
import {
  createIncomingNegotiationsSelector,
  createNegotiationDetailsToTopLevelDetailsTransformerSelector,
  createNegotiationPackagesSelector,
  isNegotiationCurve
} from 'selectors/negotiations'
import { GlobalState } from 'store'
import { getNegotiatedValue, itemNegotiationTypes, summaryNegotiationTypes } from '../../shared/utils'
import { map, sortBy, groupBy, uniq, pipe } from 'lodash/fp'

import { GridId } from 'view/components/datagrid'
import { getTradingEntityName } from 'view/shared/datagrid'
import { DateLike, dateLikeCompareAscendingFn } from 'utils'
import { createSelectedCounterpartyIdSelector } from 'selectors/selectionSelectors'
import { OrderTermHelper } from 'model'
import { CurveLeftNavigationItem } from './CurveNegotiationsLeftNavigation'

interface NegotiationDetailWithCounterpartyCollectionOrderFund extends NegotiationDetail {
  collection: Exclude<NegotiationDetail['collection'], null>
  counterparty: Exclude<NegotiationDetail['counterparty'], null>
  orderFund: Exclude<NegotiationDetail['orderFund'], null>
}

const negotationHasCollectionCounterpartyOrderFund = (n: NegotiationDetail): n is NegotiationDetailWithCounterpartyCollectionOrderFund =>
  !isNil(n.collection) && !isNil(n.counterparty) && !isNil(n.orderFund)

export const curveItemNegotiationsSelector: Selector<GlobalState, NegotiationDetailWithCounterpartyCollectionOrderFund[]> = createSelector(
  [createIncomingNegotiationsSelector(itemNegotiationTypes)],
  negotiationDetails =>
    negotiationDetails.filter(n => isNegotiationCurve(n.negotiation)).filter(negotationHasCollectionCounterpartyOrderFund)
)

const curveItemNegotiationsSummarySelector: Selector<GlobalState, NegotiationDetailWithCounterpartyCollectionOrderFund[]> = createSelector(
  [createIncomingNegotiationsSelector(summaryNegotiationTypes)],
  negotiationDetails =>
    negotiationDetails.filter(n => isNegotiationCurve(n.negotiation)).filter(negotationHasCollectionCounterpartyOrderFund)
)

/* eslint-disable-next-line */
export const incomingCurveNegotiationDetailsToTopLevelDetailsTransformerSelector = createNegotiationDetailsToTopLevelDetailsTransformerSelector(
  curveItemNegotiationsSelector,
  curveItemNegotiationsSummarySelector
)

export const curveNegotiationsByOrderFundCounterpartySelector = createSelector(
  curveItemNegotiationsSelector,
  groupBy(({ orderFund }) => orderFund.id)
)

export const curveItemNegotiationsBySelectedOrderFundId = createSelector(
  [
    curveItemNegotiationsSelector,
    curveNegotiationsByOrderFundCounterpartySelector,
    createSelectedCounterpartyIdSelector(GridId.CurvesGrid)
  ],
  (allCurveNegotiation, curveNegotiationsByCounterpartyId, orderFundId) =>
    orderFundId ? curveNegotiationsByCounterpartyId[orderFundId] || allCurveNegotiation : allCurveNegotiation
)

const groupNegotiationsIntoCurveOcoBuckets = memoize((negotiations: NegotiationDetailWithCounterpartyCollectionOrderFund[]) =>
  _groupBy(negotiations, negotiation => negotiation.collection.id + negotiation.counterparty.id)
)

const groupedCurveItemByCollectionAndCounterpartySelector = createSelector(curveItemNegotiationsBySelectedOrderFundId, negotiations =>
  groupNegotiationsIntoCurveOcoBuckets(negotiations)
)

export const curveNegotiationsTotalNumberOfCuvesSelector: Selector<GlobalState, number> = createSelector(
  curveItemNegotiationsSelector,
  negotiationEntries => Object.keys(groupNegotiationsIntoCurveOcoBuckets(negotiationEntries)).length
)

export const getTermForNegotiation = (n: Negotiation) => {
  const term = getNegotiatedValue(n.term)
  const startDate = getNegotiatedValue(n.startDate)
  const endDate = getNegotiatedValue(n.endDate)
  const isCustom = [OrderTerm.CUSTOM, OrderTerm.CUSTOM_O].includes(term)
  return isCustom ? `CUSTOM - ${startDate} / ${endDate ?? 'O'}` : OrderTermHelper.getName(term)
}

export const getAllTermsForCurveNegotiationsSelector = createSelector(
  curveItemNegotiationsSelector,
  pipe(
    map(({ negotiation }) => getTermForNegotiation(negotiation)),
    sortBy(term => term),
    uniq
  ),
  // @ts-ignore
  // redefined selector types do not include the options
  {
    // @ts-ignore
    memoizeOptions: {
      resultEqualityCheck: isEqual
    }
  }
)

type ThreeTuple = [number, number, number]
export const getSubTotalsForNegotiations = (negotiations: NegotiationDetail[]) =>
  negotiations.reduce<ThreeTuple>(
    (subTotals, { negotiation: { status } }) => {
      const idx =
        status === NegotiationStatus.REQUESTED_WITHOUT_PRICE
          ? 0
          : status === NegotiationStatus.QUOTED
          ? 1
          : status === NegotiationStatus.REQUESTED_WITH_PRICE
          ? 2
          : undefined
      if (idx === undefined) {
        return subTotals
      }
      const copy: [number, number, number] = [...subTotals]
      copy.splice(idx, 1, (copy[idx] += 1))
      return copy
    },
    [0, 0, 0]
  )

const isNotNull = <T>(v: T): v is Exclude<T, null> => !isNull(v)

const getMinRfqTimeForNegotiations = (negotiations: NegotiationDetail[] = []): DateLike => {
  const [rfq] = negotiations.map(n => n.negotiation.rfqEndTime).sort(dateLikeCompareAscendingFn)
  return rfq
}
const getMinOtwTimeForNegotiations = (negotiations: NegotiationDetail[] = []): DateLike | undefined => {
  const [otw] = negotiations
    .map(n => n.negotiation.otwEndTime)
    .filter(isNotNull)
    .sort(dateLikeCompareAscendingFn)
  return otw
}

export const curveLeftNavigationItemsSelector = createSelector(curveNegotiationsByOrderFundCounterpartySelector, negotiations =>
  Object.values(negotiations).reduce<CurveLeftNavigationItem[]>(
    (counterpartyDetails, c) => [
      ...counterpartyDetails,
      {
        counterPartyId: c[0].orderFund.id,
        label: getTradingEntityName(c[0], false),
        subTotals: getSubTotalsForNegotiations(c),
        minRfqEndTime: getMinRfqTimeForNegotiations(c),
        minOtwEndTime: getMinOtwTimeForNegotiations(c)
      }
    ],
    []
  )
)

export const curveNegotiationPackagesSelector = createNegotiationPackagesSelector(
  groupedCurveItemByCollectionAndCounterpartySelector,
  ({ negotiation }) => getTermForNegotiation(negotiation)
)
